import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';

@Component({
  selector: 'app-help',
  standalone: true,
  imports: [CommonModule, RouterLink],
  template: `
    <div class="page-container help-page">
      <div class="header-flex">
        <div>
          <h1>Guida & Manuale d'Uso Avanzato</h1>
          <p class="subtitle">Documentazione dettagliata dei flussi operativi di Rock & Rullo</p>
        </div>
      </div>

      <!-- Quick Index Cards (Fixed scroll without navigating away) -->
      <div class="help-index-grid">
        <div class="index-card card" (click)="scrollTo('flusso')">
          <span class="material-icons">account_tree</span>
          <div>
            <strong>Il Flusso Completo</strong>
            <span>Ciclo di vita della commessa</span>
          </div>
        </div>
        <div class="index-card card" (click)="scrollTo('menu')">
          <span class="material-icons">menu</span>
          <div>
            <strong>Voci di Menu & Azioni</strong>
            <span>Dettaglio schermate e bottoni</span>
          </div>
        </div>
        <div class="index-card card" (click)="scrollTo('mobile')">
          <span class="material-icons">smartphone</span>
          <div>
            <strong>Modalità Mobile (Cantiere)</strong>
            <span>Uso rapido da smartphone</span>
          </div>
        </div>
      </div>

      <!-- SEZIONE 1: IL FLUSSO DETTAGLIATO -->
      <section id="flusso" class="help-section card">
        <div class="card-swatch-top"></div>
        <div class="sec-title">
          <span class="material-icons">account_tree</span>
          <h2>1. Flusso di Lavoro Dettagliato (End-to-End)</h2>
        </div>
        <p class="sec-intro">Il gestionale è strutturato per seguire l'artigiano passo dopo passo, garantendo zero errori contabili e massima velocità operativa:</p>

        <div class="workflow-steps">
          <div class="w-step">
            <div class="w-num">1</div>
            <div class="w-content">
              <h3>Creazione e Gestione Anagrafica Cliente</h3>
              <p><strong>Come fare:</strong> Vai su <em>Clienti</em> nel menu, clicca sul pulsante blu <em>"Nuovo Cliente"</em>. Compila nome, cognome, telefono, email e indirizzo di cantiere.</p>
              <p><strong>Cosa succede:</strong> Il cliente viene salvato in rubrica. Da smartphone, nella lista clienti, potrai toccare direttamente il pulsante 📞 <em>Chiama</em> per telefonargli o 📍 <em>Mappa</em> per aprire il navigatore GPS di Google Maps verso il cantiere.</p>
            </div>
          </div>

          <div class="w-step">
            <div class="w-num">2</div>
            <div class="w-content">
              <h3>Emissione Preventivo con Numerazione Progressiva e Preset</h3>
              <p><strong>Come fare:</strong> Vai su <em>Preventivi</em> e clicca su <em>Nuovo Preventivo</em>. Seleziona il cliente e la data.</p>
              <p><strong>Cosa succede:</strong> 
                <br>• Il sistema assegna automaticamente un codice progressivo annuale (es. <code>PREV-2024-001</code>).
                <br>• Puoi inserire le voci di lavoro manualmente oppure cliccare sul pulsante ⚡ <strong>"Inserisci da Voci Frequenti"</strong> per aprire la libreria dei tuoi preset (es. idropittura lavabile, rasatura, ecc.), cercarla istantaneamente tramite la barra di ricerca e aggiungerla con un tap.
                <br>• I totali si calcolano in automatico. Clicca su <em>Salva Preventivo</em>.
              </p>
            </div>
          </div>

          <div class="w-step">
            <div class="w-num">3</div>
            <div class="w-content">
              <h3>Invio al Cliente, Duplicazione e PDF</h3>
              <p><strong>Come fare:</strong> Apri il dettaglio di un preventivo esistente.</p>
              <p><strong>Cosa succede:</strong> 
                <br>• Cliccando su <strong>PDF</strong> scarichi il documento ufficiale formattato con i dati aziendali, la tua Partita IVA e le note fiscali salvate nel Centro Bottega.
                <br>• Cliccando su <strong>Email</strong> il preventivo viene spedito direttamente all'indirizzo del cliente.
                <br>• Cliccando su <strong>Duplica</strong>, il sistema clona tutte le voci di lavoro e i dati in un nuovo preventivo, permettendoti di selezionare un cliente differente per lavori simili, azzerando i tempi di scrittura.
              </p>
            </div>
          </div>

          <div class="w-step">
            <div class="w-num">4</div>
            <div class="w-content">
              <h3>Trasformazione in Fattura e Blocco di Sicurezza</h3>
              <p><strong>Come fare:</strong> Quando il cliente approva il preventivo, aprilo e clicca sul pulsante verde <strong>"Trasforma in Fattura"</strong>.</p>
              <p><strong>Cosa succede:</strong> 
                <br>• Vengono copiati in automatico tutti i dati del cliente e le voci di lavoro in una nuova fattura, collegando indissolubilmente la fattura al preventivo di origine (il cui numero apparirà sia a schermo che nel PDF della fattura).
                <br>• <strong>Blocco automatico:</strong> Il preventivo originario viene bloccato in sola lettura (apparirà l'icona di un lucchetto) per impedire modifiche accidentali che alterino la contabilità.
                <br>• Se per motivi eccezionali devi modificarlo, comparirà il pulsante <em>"Sblocca Modifiche"</em> che richiederà una conferma esplicita tramite modale di sicurezza.
              </p>
            </div>
          </div>

          <div class="w-step">
            <div class="w-num">5</div>
            <div class="w-content">
              <h3>Programmazione Cantieri (Agenda)</h3>
              <p><strong>Come fare:</strong> Vai su <em>Agenda</em> per visualizzare il calendario mensile.</p>
              <p><strong>Cosa succede:</strong> Cliccando sul pulsante <strong>"+"</strong> sopra qualsiasi giorno, puoi programmare sopralluoghi o giornate di cantiere, associandoli al cliente e tenendo traccia dell'orario e dello stato (programmato, completato, annullato).</p>
            </div>
          </div>
        </div>
      </section>

      <!-- SEZIONE 2: VOCI DI MENU E AZIONI -->
      <section id="menu" class="help-section card">
        <div class="card-swatch-top" style="background: var(--accent);"></div>
        <div class="sec-title">
          <span class="material-icons">menu</span>
          <h2>2. Dettaglio Voci di Menu & Azioni Specifiche</h2>
        </div>
        <p class="sec-intro">Guida puntuale su ogni sezione del gestionale, cosa visualizza e come interagire:</p>

        <div class="menu-guides-grid">
          
          <div class="m-guide-box">
            <div class="mg-header">
              <span class="material-icons">dashboard</span>
              <h3>Dashboard (Home)</h3>
            </div>
            <ul>
              <li><strong>Cosa mostra:</strong> Card riassuntive con preventivi attivi, bozze di fattura, cantieri odierni e fatturato del mese corrente.</li>
              <li><strong>Azioni:</strong> 
                <br>• Clicca su <em>"Crea Nuovo Preventivo"</em> per iniziare subito.
                <br>• Clicca sui contatori del riepilogo rapido per accedere direttamente agli elenchi filtrati.
              </li>
            </ul>
          </div>

          <div class="m-guide-box">
            <div class="mg-header">
              <span class="material-icons">description</span>
              <h3>Preventivi</h3>
            </div>
            <ul>
              <li><strong>Cosa mostra:</strong> Elenco completo dei preventivi con codice progressivo (es. <code>PREV-2024-001</code>), cliente, data, stato (bozza, accettato, scaduto, annullato) e importo totale.</li>
              <li><strong>Azioni:</strong> 
                <br>• Usa la barra di ricerca per trovare clienti o numeri.
                <br>• Seleziona l'anno dal menu a tendina per filtrare l'archivio storico.
                <br>• Clicca sull'icona 👁️ per visualizzare/modificare, o 🗑️ per eliminare (previa conferma).
              </li>
            </ul>
          </div>

          <div class="m-guide-box">
            <div class="mg-header">
              <span class="material-icons">receipt_long</span>
              <h3>Fatture & Contabilità</h3>
            </div>
            <ul>
              <li><strong>Cosa mostra:</strong> Elenco delle fatture e fatture proforma, con evidenza del numero documento, tipo, scadenze, stati (bozza, emessa, pagata) e indicazione del preventivo di origine (es. <em>Rif. Preventivo: PREV-2024-001</em>).</li>
              <li><strong>Azioni:</strong> Clicca su <em>Nuova Fattura</em> per crearne una da zero, oppure apri il dettaglio per scaricare il PDF ufficiale.</li>
            </ul>
          </div>

          <div class="m-guide-box">
            <div class="mg-header">
              <span class="material-icons">event</span>
              <h3>Agenda & Cantieri</h3>
            </div>
            <ul>
              <li><strong>Cosa mostra:</strong> Calendario interattivo mensile e vista elenco.</li>
              <li><strong>Azioni:</strong> 
                <br>• Clicca sulle frecce ⟨ ⟩ per cambiare mese.
                <br>• Clicca sul tasto <strong>Oggi</strong> per tornare alla data odierna.
                <br>• Clicca su qualsiasi giorno per visualizzare i dettagli dei cantieri previsti in quella data.
              </li>
            </ul>
          </div>

          <div class="m-guide-box">
            <div class="mg-header">
              <span class="material-icons">people</span>
              <h3>Clienti (Rubrica)</h3>
            </div>
            <ul>
              <li><strong>Cosa mostra:</strong> Elenco anagrafico con contatti e indirizzi.</li>
              <li><strong>Azioni:</strong> 
                <br>• Clicca su <em>Nuovo Cliente</em> per aggiungere un contatto.
                <br>• Clicca su ✏️ per modificare o 🗑️ per eliminare.
                <br>• Da mobile, usa i pulsanti rapidi 📞 <em>Chiama</em> e 📍 <em>Mappa</em> direttamente sulla card del cliente.
              </li>
            </ul>
          </div>

          <div class="m-guide-box">
            <div class="mg-header">
              <span class="material-icons">settings</span>
              <h3>Centro Bottega (Impostazioni)</h3>
            </div>
            <ul>
              <li><strong>Cosa mostra:</strong> Pannello suddiviso in 4 tab:
                <br>1. <strong>Profilo & Azienda:</strong> Dati fiscali e IBAN per i PDF.
                <br>2. <strong>Voci Frequenti:</strong> Gestione del catalogo preset di lavoro.
                <br>3. <strong>Spese Materiali:</strong> Registro scontrini e costi del colorificio.
                <br>4. <strong>Foto Cantiere:</strong> Archivio foto "Prima" e "Dopo" l'intervento.
              </li>
            </ul>
          </div>

        </div>
      </section>

      <!-- SEZIONE 3: MOBILE -->
      <section id="mobile" class="help-section card">
        <div class="card-swatch-top" style="background: var(--success);"></div>
        <div class="sec-title">
          <span class="material-icons">smartphone</span>
          <h2>3. Modalità Mobile (Uso in Cantiere)</h2>
        </div>
        <p class="sec-intro">L'app si adatta automaticamente allo smartphone per offrirti un'esperienza d'uso immediata e senza distrazioni:</p>
        
        <div class="mobile-tips-list">
          <div class="m-tip">
            <span class="material-icons">touch_app</span>
            <div>
              <strong>Barra di Navigazione Inferiore:</strong>
              <p>Sullo schermo dello smartphone trovi sempre in basso le scorciatoie per <em>Home</em>, <em>Agenda</em>, <em>Preventivi</em> e <em>Clienti</em>. Per accedere a Fatture, Impostazioni e Guide, tocca il pulsante <strong>⋯ Altro</strong>.</p>
            </div>
          </div>
          <div class="m-tip">
            <span class="material-icons">view_agenda</span>
            <div>
              <strong>Voci di Lavoro a Card Verticali:</strong>
              <p>Nei moduli di preventivo e fattura, le righe di lavoro si dispongono in comode schede verticali separate (niente tabelle larghe orizzontali), perfette per essere compilate rapidamente a una mano.</p>
            </div>
          </div>
          <div class="m-tip">
            <span class="material-icons">more_vert</span>
            <div>
              <strong>Menu "Azioni Documento":</strong>
              <p>Nel dettaglio di un preventivo, i pulsanti di PDF, Email e Duplica sono raccolti in alto a destra nel menu a tendina <em>"Azioni Documento"</em> per mantenere lo schermo pulito.</p>
            </div>
          </div>
        </div>
      </section>

    </div>
  `,
  styles: [`
    .page-container { max-width: 1000px; margin: 0 auto; padding-bottom: 50px; }
    .header-flex { margin-bottom: 24px; }
    h1 { margin: 0; color: var(--text-main); font-size: 28px; font-weight: 800; letter-spacing: -0.5px; }
    .subtitle { color: var(--text-muted); margin: 4px 0 0; font-size: 14px; }

    /* Index Cards */
    .help-index-grid {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 16px;
      margin-bottom: 28px;
    }
    @media (max-width: 768px) {
      .help-index-grid { grid-template-columns: 1fr; }
    }
    .index-card {
      display: flex;
      align-items: center;
      gap: 14px;
      padding: 18px 20px;
      text-decoration: none;
      color: var(--text-main);
      border-radius: 14px;
      cursor: pointer;
      transition: transform 0.2s, border-color 0.2s;
    }
    .index-card:hover {
      transform: translateY(-2px);
      border-color: var(--primary);
    }
    .index-card .material-icons {
      font-size: 28px;
      color: var(--primary);
      background: var(--primary-light);
      padding: 10px;
      border-radius: 12px;
    }
    .index-card strong { display: block; font-size: 15px; font-weight: 700; color: var(--text-main); }
    .index-card span { font-size: 12px; color: var(--text-muted); }

    /* Help Sections */
    .help-section {
      padding: 32px;
      margin-bottom: 24px;
    }
    .sec-title {
      display: flex;
      align-items: center;
      gap: 12px;
      margin-bottom: 12px;
      color: var(--primary);
    }
    .sec-title .material-icons { font-size: 24px; }
    .sec-title h2 { margin: 0; font-size: 20px; font-weight: 800; color: var(--text-main); }
    .sec-intro { color: var(--text-muted); font-size: 14px; margin-bottom: 24px; line-height: 1.5; }

    /* Workflow Steps */
    .workflow-steps {
      display: flex;
      flex-direction: column;
      gap: 20px;
    }
    .w-step {
      display: flex;
      gap: 18px;
      align-items: flex-start;
      background: var(--bg-main);
      padding: 20px;
      border-radius: 14px;
      border: 1px solid var(--border);
    }
    .w-num {
      width: 32px;
      height: 32px;
      border-radius: 50%;
      background: var(--primary);
      color: white;
      display: flex;
      align-items: center;
      justify-content: center;
      font-weight: 800;
      font-size: 14px;
      flex-shrink: 0;
    }
    .w-content h3 { margin: 0 0 6px 0; font-size: 16px; font-weight: 700; color: var(--text-main); }
    .w-content p { margin: 4px 0; font-size: 13px; color: var(--text-muted); line-height: 1.5; }
    .w-content strong { color: var(--text-main); }

    /* Menu Guides Grid */
    .menu-guides-grid {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 20px;
    }
    @media (max-width: 768px) {
      .menu-guides-grid { grid-template-columns: 1fr; }
    }
    .m-guide-box {
      background: var(--bg-main);
      border: 1px solid var(--border);
      border-radius: 14px;
      padding: 20px;
    }
    .mg-header {
      display: flex;
      align-items: center;
      gap: 10px;
      margin-bottom: 12px;
      color: var(--primary);
    }
    .mg-header .material-icons { font-size: 22px; }
    .mg-header h3 { margin: 0; font-size: 16px; font-weight: 700; color: var(--text-main); }
    .m-guide-box ul, .m-guide-box ol {
      margin: 0;
      padding-left: 18px;
      font-size: 13px;
      color: var(--text-muted);
      line-height: 1.6;
    }
    .m-guide-box li { margin-bottom: 8px; }
    .m-guide-box strong { color: var(--text-main); }

    /* Mobile Tips */
    .mobile-tips-list {
      display: flex;
      flex-direction: column;
      gap: 16px;
    }
    .m-tip {
      display: flex;
      align-items: flex-start;
      gap: 16px;
      background: var(--bg-main);
      padding: 18px 20px;
      border-radius: 14px;
      border: 1px solid var(--border);
    }
    .m-tip .material-icons {
      font-size: 26px;
      color: var(--success);
      background: rgba(13, 148, 136, 0.12);
      padding: 10px;
      border-radius: 12px;
    }
    .m-tip strong { display: block; font-size: 15px; font-weight: 700; color: var(--text-main); margin-bottom: 4px; }
    .m-tip p { margin: 0; font-size: 13px; color: var(--text-muted); line-height: 1.4; }
  `]
})
export class HelpComponent {
  scrollTo(id: string) {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }
}
